import { z } from 'zod';
import { agentInterfaceSchema } from './agents.js';
import { discoveryResultMessageSchema, discoveryScanMessageSchema } from './discovery.js';
import { updateCheckMessageSchema, updateResultMessageSchema } from './agent-update.js';
/**
 * Типы кадров туннеля (ТЗ §9.2).
 *
 * Кадр: байт 0 — тип, байты 1–4 — идентификатор потока (uint32 big-endian,
 * 0 = управляющий канал), байты 5.. — полезная нагрузка.
 */
export const FrameType = {
    /** UTF-8 JSON управляющего сообщения, см. §9.3. */
    CONTROL: 0x01,
    /** Сервер → агент. JSON `{ host, port }`. */
    STREAM_OPEN: 0x02,
    /** Агент → сервер. Пустой. */
    STREAM_OPEN_OK: 0x03,
    /** Агент → сервер. UTF-8 текст ошибки. */
    STREAM_OPEN_ERR: 0x04,
    /** Сырые байты TCP. */
    STREAM_DATA: 0x05,
    /** Закрытие потока. Пустой. */
    STREAM_CLOSE: 0x06,
    /** uint32: сколько байт освободилось у получателя. */
    STREAM_WINDOW: 0x07,
};
export const FRAME_HEADER_SIZE = 5;
/** Управляющий канал: кадры, не относящиеся ни к одному потоку. */
export const CONTROL_STREAM_ID = 0;
/**
 * Окно на поток — 256 КБ (§9.2).
 *
 * Контроль потока обязателен: без него один быстрый видеопоток забьёт туннель
 * и заблокирует VNC, потому что все потоки делят одно WebSocket-соединение.
 */
export const STREAM_WINDOW_SIZE = 256 * 1024;
/**
 * Предел размера кадра. Защищает от исчерпания памяти на подложном кадре
 * с огромной заявленной длиной: агент — доверенный, но соединение может
 * перехватить кто угодно, а память сервера общая для всех площадок.
 */
export const MAX_FRAME_PAYLOAD = 1024 * 1024;
// — Управляющие сообщения (§9.3) ——————————————————————————————
/** Агент → сервер, сразу после подключения. */
export const helloMessageSchema = z.object({
    t: z.literal('hello'),
    agentId: z.uuid(),
    version: z.string().max(32),
    platform: z.string().max(32),
    interfaces: z.array(agentInterfaceSchema).max(64).optional(),
});
/** Агент → сервер, каждые 15 секунд (§9.1). */
export const heartbeatMessageSchema = z.object({
    t: z.literal('heartbeat'),
    uptimeSec: z.number().nonnegative(),
    cpuPercent: z.number().min(0).max(100).optional(),
    memMb: z.number().nonnegative().optional(),
});
/** Сервер → агент: запрос текущей конфигурации. */
export const configGetMessageSchema = z.object({
    t: z.literal('config.get'),
});
/**
 * Задание проверки в том виде, в каком его получает агент (§9.3).
 *
 * Порогов здесь нет намеренно: их применяет сервер (§9.4). Агент присылает
 * сырой результат, поэтому изменение порога не требует переконфигурирования
 * агентов.
 */
export const monitorTaskSchema = z.object({
    id: z.uuid(),
    kind: z.enum(['icmp', 'tcp', 'http', 'snmp']),
    target: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535).nullable().optional(),
    intervalSec: z.number().int().min(5).max(86_400),
    timeoutMs: z.number().int().min(100).max(60_000),
    /** Параметры, зависящие от типа проверки; разбирает их агент. */
    config: z.record(z.string(), z.unknown()).optional(),
});
/**
 * Сервер → агент: полный список проверок. Отправляется при подключении.
 * Предел в 10 000 — та же величина, что и кольцевой буфер телеметрии (§9.3):
 * площадка, где проверок больше, требует второго агента, а не большего кадра.
 */
export const monitorsSetMessageSchema = z.object({
    t: z.literal('monitors.set'),
    monitors: z.array(monitorTaskSchema).max(10_000),
});
/** Сервер → агент: точечное изменение. */
export const monitorsPatchMessageSchema = z.object({
    t: z.literal('monitors.patch'),
    upsert: z.array(monitorTaskSchema).max(10_000).default([]),
    remove: z.array(z.uuid()).max(10_000).default([]),
});
/** Сервер → агент: внеочередная проверка. */
export const checkNowMessageSchema = z.object({
    t: z.literal('check.now'),
    monitorId: z.uuid(),
});
/**
 * Результат одной проверки (§9.3).
 *
 * `rttMs` и `errorCode` не связаны схемой жёстко: неудачная проверка может
 * нести время до отказа, а успешная — нет, если пробник его не измеряет.
 */
export const monitorSampleSchema = z.object({
    monitorId: z.uuid(),
    ts: z.iso.datetime(),
    ok: z.boolean(),
    rttMs: z.number().nonnegative().max(600_000).nullable().optional(),
    errorCode: z.string().max(200).nullable().optional(),
});
/**
 * Агент → сервер: пачка результатов. Раз в 5 секунд либо при накоплении
 * 200 записей (§9.3). Предел в 10 000 — размер кольцевого буфера агента:
 * ровно столько он может накопить за время обрыва связи и прислать разом.
 */
export const samplesMessageSchema = z.object({
    t: z.literal('samples'),
    items: z.array(monitorSampleSchema).max(10_000),
});
/**
 * Сообщения агента. Разбираются на сервере Zod-схемой без исключений (§6.1):
 * агент — внешняя граница, пусть и доверенная.
 */
export const agentMessageSchema = z.discriminatedUnion('t', [
    helloMessageSchema,
    heartbeatMessageSchema,
    samplesMessageSchema,
    discoveryResultMessageSchema,
    updateResultMessageSchema,
]);
export const serverMessageSchema = z.discriminatedUnion('t', [
    configGetMessageSchema,
    monitorsSetMessageSchema,
    monitorsPatchMessageSchema,
    checkNowMessageSchema,
    discoveryScanMessageSchema,
    updateCheckMessageSchema,
]);
/** Полезная нагрузка кадра `STREAM_OPEN`. */
export const streamOpenSchema = z.object({
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535),
});
//# sourceMappingURL=tunnel.js.map